import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronDown, ChevronUp, Download, ExternalLink, FileText, Sparkles } from "lucide-react";
import { EmptyState, ErrorState, LoadingState } from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCalendarDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import { money, num, SOURCE_LABEL, sourceEventLabel, sourceHref } from "./shared";
import { lastDay, monthLabel, monthOf, monthsBetween, periodLabel, type Month, type Period } from "./period";
import type { ReportingBasis } from "./types";

type Read<T> = Promise<{ data?: T; error?: unknown; response: Response }>;

/** Several reads as one, for useLoad: every answer in order, or the first failure. */
export async function readAll<T>(reads: Array<Read<T>>): Promise<{ data?: T[]; error?: unknown; response: Response }> {
  const results = await Promise.all(reads);
  const failed = results.find((r) => r.error !== undefined || !r.response.ok);
  if (failed) return { error: failed.error ?? new Error(`HTTP ${failed.response.status}`), response: failed.response };
  return { data: results.map((r) => r.data as T), response: results[0]?.response ?? new Response(null, { status: 200 }) };
}

/** A month table's frozen first column. Opaque, with an opaque table under it, so figures scroll beneath without a seam. */
export const STICKY_CELL = "sticky left-0 z-10 bg-card";
export const MONTH_TABLE = "bg-card backdrop-blur-none";
/** The month table's pinned right-hand column: Total, or the latest month. */
export const PINNED_CELL = "sticky right-0 z-10 border-l bg-card";
/** Header, section and footer rows of a month table: solid, so their frozen cell matches the rest of the row. */
export const SOLID_ROW = "bg-muted hover:bg-muted";

// shortcut: monthly views read one report per month in parallel; a server-side monthly endpoint once books get big.
export const MAX_MONTHS = 24;

/** A month's column label: "Mar 2026", or "Oct MTD" while the month is still running. */
export function monthColumnLabel(month: Month, asAt: string) {
  return monthOf(asAt) === month && asAt < lastDay(month) ? `${monthLabel(month, false)} MTD` : monthLabel(month);
}

/** The as-at date of each month in the period: its last day, or the period's as-at for a month still running. */
export function monthEnds(period: Pick<Period, "from" | "to" | "asAt">, months: Month[]) {
  return months.map((m) => (lastDay(m) > period.asAt ? period.asAt : lastDay(m)));
}

/** A wide month table opens scrolled to its latest months, where the reader looks first. */
export function useScrollToEnd<T extends HTMLElement>(ready: unknown) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const scroller = ref.current?.querySelector<HTMLElement>("[data-slot=table-container]");
    if (scroller) scroller.scrollLeft = scroller.scrollWidth;
  }, [ready]);
  return ref;
}

export type ReportView = "total" | "monthly" | "quarterly";

const VIEW_LABEL: Record<ReportView, string> = { total: "Total", monthly: "Monthly", quarterly: "Quarterly" };

/** Total | Monthly | Quarterly, kept in the URL by the caller. */
export function ViewToggle({ value, onChange }: { value: ReportView; onChange: (v: ReportView) => void }) {
  return (
    <div role="radiogroup" aria-label="Columns" className="inline-flex h-8 items-center rounded-md bg-muted p-0.5 text-muted-foreground">
      {(["total", "monthly", "quarterly"] as const).map((v) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={value === v}
          onClick={() => value !== v && onChange(v)}
          className={cn(
            "rounded-sm px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            value === v ? "bg-background text-foreground shadow-sm" : "hover:text-foreground",
          )}
        >
          {VIEW_LABEL[v]}
        </button>
      ))}
    </div>
  );
}

/** One column of a monthly or quarterly report. */
export interface Bucket {
  label: string;
  /** First and last day, for a flow (the P&L). */
  from: string;
  to: string;
  /** The day a balance is read at: the last day, or the period's as-at while it is still running. */
  asAt: string;
  caption: string;
}

/**
 * The period's columns: a month each, or a quarter of the financial year each. A quarter cut by the period's edge
 * keeps only the period's months, and says so in its label.
 */
export function buckets(period: Pick<Period, "from" | "to" | "asAt" | "fyStart">, view: "monthly" | "quarterly"): Bucket[] {
  const months = monthsBetween(period.from, period.to);
  const groups: Month[][] = [];
  for (const m of months) {
    const month = Number(m.slice(5, 7));
    // Quarters of the financial year: a book whose year starts in April has Apr-Jun as its first.
    const key = Math.floor(((month - period.fyStart + 12) % 12) / 3);
    const last = groups[groups.length - 1];
    const prev = last?.[last.length - 1];
    const prevKey = prev ? Math.floor(((Number(prev.slice(5, 7)) - period.fyStart + 12) % 12) / 3) : null;
    if (view === "quarterly" && last && prevKey === key && last.length < 3) last.push(m);
    else groups.push([m]);
  }
  return groups.map((ms) => {
    const first = ms[0]!;
    const end = ms[ms.length - 1]!;
    const asAt = lastDay(end) > period.asAt ? period.asAt : lastDay(end);
    return {
      label: ms.length === 1 ? monthColumnLabel(first, period.asAt) : `${monthLabel(first, false)} – ${monthColumnLabel(end, period.asAt)}`,
      from: `${first}-01`,
      to: lastDay(end),
      asAt,
      caption: ms.length === 1 ? monthLabel(first) : periodLabel(first, end),
    };
  });
}

export interface ChartSeries {
  label: string;
  /** Tailwind background class for the bar and its key. */
  className: string;
  values: number[];
}

/**
 * Grouped month bars, the reports' one chart. A zero line splits gains from losses; hovering a month reads its figures
 * out in the header instead of a floating tooltip, so nothing covers the bars.
 */
export function MonthBars({
  labels,
  series,
  summary,
  heightClass = "h-[132px]",
  openLast,
  onPick,
}: {
  labels: string[];
  series: ChartSeries[];
  /** The readout when no month is hovered, e.g. the period's totals. */
  summary?: ReactNode;
  /** Tailwind height for the bars, so a page can shorten them in a narrow pane. */
  heightClass?: string;
  /** The last bar is a month still running (Mercury shades the open period): its figures are to date. */
  openLast?: boolean;
  onPick?: (index: number) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const open = (i: number) => openLast && i === labels.length - 1;
  const all = series.flatMap((s) => s.values);
  const up = Math.max(0, ...all);
  const down = Math.max(0, ...all.map((v) => -v));
  const span = up + down || 1;
  const pct = (v: number) => `${(Math.abs(v) / span) * 100}%`;
  return (
    <figure className="space-y-2">
      <figcaption className="flex min-h-5 flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {series.map((s, i) => (
          <span key={s.label} className="inline-flex items-center gap-1.5">
            <span className={cn("size-2 rounded-full", s.className)} />
            {s.label}
            {hover !== null ? <span className="font-medium tabular-nums text-foreground">{money(series[i]!.values[hover])}</span> : null}
          </span>
        ))}
        <span className="ml-auto tabular-nums">{hover !== null ? `${labels[hover]}${open(hover) ? " to date" : ""}` : summary}</span>
      </figcaption>
      <div className={cn("relative flex items-stretch gap-1", heightClass)} onMouseLeave={() => setHover(null)}>
        {/* The zero line. */}
        <div className="pointer-events-none absolute inset-x-0 border-t border-border" style={{ top: pct(up) }} />
        {labels.map((label, i) => (
          <button
            key={label}
            type="button"
            onMouseEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
            onClick={onPick ? () => onPick(i) : undefined}
            aria-label={`${label}${open(i) ? " to date" : ""}: ${series.map((s) => `${s.label} ${money(s.values[i])}`).join(", ")}`}
            className={cn(
              "flex min-w-0 flex-1 flex-col rounded-sm px-[2px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              open(i) && "bg-muted/40 opacity-60",
              hover === i && "bg-muted/60",
              !onPick && "cursor-default",
            )}
          >
            <span className="flex w-full items-end justify-center gap-[2px]" style={{ height: pct(up) }}>
              {series.map((s) => (
                <span key={s.label} className={cn("w-full max-w-3 rounded-t-[2px]", s.className)} style={{ height: s.values[i]! > 0 ? `${(s.values[i]! / (up || 1)) * 100}%` : 0 }} />
              ))}
            </span>
            <span className="flex w-full items-start justify-center gap-[2px]" style={{ height: pct(down) }}>
              {series.map((s) => (
                <span key={s.label} className={cn("w-full max-w-3 rounded-b-[2px] opacity-60", s.className)} style={{ height: s.values[i]! < 0 ? `${(-s.values[i]! / (down || 1)) * 100}%` : 0 }} />
              ))}
            </span>
          </button>
        ))}
      </div>
      <div className="flex gap-1 text-[10px] text-muted-foreground">
        {labels.map((label, i) => (
          // The month alone under its bar (the readout above has the full label); beyond 12, every other one.
          <span key={label} className="min-w-0 flex-1 overflow-hidden text-center whitespace-nowrap">
            {labels.length <= 12 || i % 2 === 0 ? label.split(" ")[0] : ""}
          </span>
        ))}
      </div>
    </figure>
  );
}

/** A report section's heading with the way into the full report. */
export function SectionHeading({ title, to, cta, children }: { title: string; to?: string; cta?: string; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h2 className="flex items-center gap-2 text-base font-semibold">
        {title}
        {children}
      </h2>
      {to ? (
        <Link to={to} className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
          {cta ?? "View report"} <ArrowRight className="size-3.5" />
        </Link>
      ) : null}
    </div>
  );
}

/** One figure a report can open: an account over a range of days, or (no `from`) everything up to `to`. */
export interface DrillTarget {
  accountId: string;
  label: string;
  /** First day, YYYY-MM-DD; omitted for a balance, which is everything from the book's start. */
  from?: string;
  to: string;
  /** The figure clicked, shown as the panel's total. */
  amount: number | string | null | undefined;
  /** Credit for revenue, liability and equity accounts: their lines read credit positive, as the report does. */
  natural: "debit" | "credit";
  /** A second line under the title, e.g. "Mar 2026" or "As at 31 Oct 2026". */
  caption: string;
  /** The connected bank account, when the account is a bank account: its transactions are the account's own. */
  bankAccountId?: string;
  /** The accounting platform's figure, when the report was compared: read the same way as `amount`. */
  platform?: { amount: number; difference: number };
}

/**
 * The accounting platform the reports check against: its name ("Xero") and whether it can be read now. Undefined until
 * read, null when the read failed. Read once per page and passed down: each read refreshes the connection with the vendor.
 */
export function usePlatform() {
  const { platform } = useBooks();
  const connection = useLoad(() => platform.GET("/v1/accounting/connection", {}), [platform]);
  const c = connection.data;
  if (connection.error) return null;
  return c ? { name: c.serviceDisplayName ?? null, connected: c.status === "Callable" } : undefined;
}

export type Platform = ReturnType<typeof usePlatform>;

export const platformName = (platform: Platform) => platform?.name ?? "your platform";

/**
 * Mercury-style report drill-down: the figure's journal lines in a side panel over the report, stepping through the
 * report's figures with previous and next, with the way into the general ledger for the rest.
 */
export function useDrilldown(period: Pick<Period, "months" | "basis">, platform?: Platform) {
  const [targets, setTargets] = useState<DrillTarget[]>([]);
  const [index, setIndex] = useState<number | null>(null);
  const open = (list: DrillTarget[], at: number) => {
    setTargets(list);
    setIndex(at);
  };
  const target = index === null ? null : (targets[index] ?? null);
  const sheet = (
    // Not modal, after Mercury: the report stays readable and clickable beside the panel, and another figure replaces this one.
    <Sheet modal={false} open={target !== null} onOpenChange={(o) => !o && setIndex(null)}>
      <SheetContent className="w-full gap-0 overflow-y-auto shadow-2xl sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        {target ? (
          <DrillPanel
            key={`${target.accountId}:${target.from}:${target.to}`}
            target={target}
            period={period}
            platformName={platform?.name ?? undefined}
            onPrevious={index! > 0 ? () => setIndex(index! - 1) : undefined}
            onNext={index! < targets.length - 1 ? () => setIndex(index! + 1) : undefined}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
  return { open, sheet, openKey: target ? `${target.accountId}:${target.from ?? ""}:${target.to}` : null };
}

export const drillKey = (t: Pick<DrillTarget, "accountId" | "from" | "to">) => `${t.accountId}:${t.from ?? ""}:${t.to}`;

const DRILL_LIMIT = 100;

function DrillPanel({
  target,
  period,
  platformName,
  onPrevious,
  onNext,
}: {
  target: DrillTarget;
  period: Pick<Period, "months" | "basis">;
  platformName?: string;
  onPrevious?: () => void;
  onNext?: () => void;
}) {
  const { books, links } = useBooks();
  const navigate = useNavigate();
  const from = target.from ?? (period.months[0] ? `${period.months[0]}-01` : undefined);
  const entries = useLoad(
    () =>
      books.GET("/v1/books/current/journal-entries", {
        params: { query: { AccountId: target.accountId, From: from, To: target.to, Limit: DRILL_LIMIT, Basis: period.basis } },
      }),
    [books, target.accountId, from, target.to, period.basis],
  );
  const sign = target.natural === "credit" ? -1 : 1;
  const lines = (entries.data?.data ?? []).flatMap((e) =>
    (e.lines ?? [])
      .filter((l) => l.ledgerAccountId === target.accountId)
      .map((l) => ({ entry: e, line: l, amount: sign * (num(l.debit) - num(l.credit)) })),
  );
  const more = Boolean(entries.data?.meta?.cursors?.next);
  const ledgerHref = ledgerLink(target.accountId, from, target.to, period.basis);

  return (
    <>
      <SheetHeader className="flex-row items-start gap-3 border-b pr-12">
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Report drill-down</p>
          <SheetTitle className="truncate">{target.label}</SheetTitle>
          <SheetDescription className="flex flex-wrap items-baseline justify-between gap-x-3">
            <span>{target.caption}</span>
            <span className="text-base font-semibold tabular-nums text-foreground">{money(target.amount)}</span>
          </SheetDescription>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button size="icon" variant="outline" className="size-7" disabled={!onPrevious} onClick={onPrevious} aria-label="Previous figure">
            <ChevronUp className="size-4" />
          </Button>
          <Button size="icon" variant="outline" className="size-7" disabled={!onNext} onClick={onNext} aria-label="Next figure">
            <ChevronDown className="size-4" />
          </Button>
        </div>
      </SheetHeader>
      <div className="space-y-3 p-4">
        {target.platform ? <PlatformComparison target={target} platformName={platformName} /> : null}
        {entries.loading && !entries.data ? (
          <LoadingState label="Loading the lines" />
        ) : entries.error ? (
          <ErrorState message={apiErrorMessage(entries.error, "Couldn't load the lines.")} onRetry={entries.reload} />
        ) : lines.length === 0 ? (
          <EmptyState icon={FileText} title="No lines in this range">
            The figure comes from opening balances or earlier years.
          </EmptyState>
        ) : (
          <ol className="divide-y rounded-lg border">
            {lines.map(({ entry, line, amount }) => {
              // A payment's lines open the invoice or bill it paid, as the general ledger does.
              const docType = entry.documentType ?? entry.sourceType;
              const doc = sourceHref(links, docType, entry.documentId ?? entry.sourceId);
              return (
                <li key={`${entry.id}:${line.lineNumber}`} className="flex items-stretch">
                  <button
                    type="button"
                    onClick={() => navigate(`${ledgerHref}&entry=${entry.id}`)}
                    className="grid min-w-0 flex-1 grid-cols-[4.75rem_1fr_auto] items-baseline gap-x-3 py-2 pl-3 pr-2 text-left text-sm transition hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                  >
                    <span className="text-xs tabular-nums text-muted-foreground">{formatCalendarDate(entry.entryDate ?? "")}</span>
                    <span className="min-w-0">
                      <span className="block truncate">{entry.description || "Journal entry"}</span>
                      <span className="block truncate text-xs text-muted-foreground">{sourceEventLabel(entry.sourceType, entry.sourceEvent)}</span>
                    </span>
                    <span className={cn("tabular-nums", amount < 0 && "text-muted-foreground")}>{money(amount)}</span>
                  </button>
                  {doc ? (
                    <Link
                      to={doc}
                      aria-label={`Open the ${(SOURCE_LABEL[docType!] ?? "document").toLowerCase()}`}
                      className="grid w-8 shrink-0 place-items-center text-muted-foreground transition hover:bg-muted/50 hover:text-foreground focus-visible:bg-muted/50 focus-visible:outline-none"
                    >
                      <ArrowUpRight className="size-3.5" />
                    </Link>
                  ) : (
                    <span className="w-8 shrink-0" />
                  )}
                </li>
              );
            })}
          </ol>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            {more ? `The latest ${DRILL_LIMIT} entries.` : lines.length > 0 ? `${lines.length} line${lines.length === 1 ? "" : "s"}.` : null}
          </span>
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link
              // A bank account's own transactions; for any other account, the bank transactions filed to it.
              to={`/books/bank-lines?${new URLSearchParams({
                ...(target.bankAccountId ? { account: target.bankAccountId } : { ledgerAccount: target.accountId }),
                ...(from ? { from: monthOf(from) } : {}),
                to: monthOf(target.to),
              })}`}
              className="font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Bank transactions
            </Link>
            <Link to={ledgerHref} className="inline-flex items-center gap-1 font-medium text-foreground underline-offset-4 hover:underline">
              Open in the general ledger <ExternalLink className="size-3" />
            </Link>
          </span>
        </div>
      </div>
    </>
  );
}

/**
 * The books beside the accounting platform for this figure: Dolfin's edge over a ledger alone. A difference gets its
 * explanation and a way to ask the agent about it.
 */
function PlatformComparison({ target, platformName }: { target: DrillTarget; platformName?: string }) {
  const { askAgent } = useBooks();
  const name = platformName ?? "Your platform";
  const p = target.platform!;
  const differs = Math.abs(p.difference) >= 0.005;
  return (
    <section
      className={cn(
        "space-y-2 rounded-lg border p-3 text-sm",
        differs ? "border-amber-500/40 bg-amber-500/5" : "border-emerald-500/30 bg-emerald-500/5",
      )}
    >
      <dl className="grid grid-cols-3 gap-2">
        <div>
          <dt className="text-xs text-muted-foreground">Books</dt>
          <dd className="font-medium tabular-nums">{money(target.amount)}</dd>
        </div>
        <div>
          <dt className="truncate text-xs text-muted-foreground">{name}</dt>
          <dd className="font-medium tabular-nums">{money(p.amount)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Difference</dt>
          <dd className={cn("font-semibold tabular-nums", differs ? "text-amber-800 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-400")}>
            {differs ? money(p.difference) : "Agrees"}
          </dd>
        </div>
      </dl>
      {differs ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">Activity in {name === "Your platform" ? "your platform" : name} that is not in these books, such as journals entered there directly.</p>
          {askAgent ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                askAgent(
                  `Why does ${target.label} differ between my books and ${name} (${target.caption})? The books show ${money(target.amount)} and ${name} shows ${money(p.amount)}.`,
                )
              }
            >
              <Sparkles className="size-3.5" /> Ask the agent why
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function ledgerLink(accountId: string, from: string | undefined, to: string, basis: ReportingBasis) {
  const q = new URLSearchParams({ accountId, ...(from ? { from: monthOf(from) } : {}), to: monthOf(to), basis });
  return `/books/ledger?${q}`;
}

/** Downloads rows as a CSV file, as a spreadsheet opens it. */
export function downloadCsv(filename: string, rows: Array<Array<string | number | null | undefined>>) {
  const cell = (v: string | number | null | undefined) => {
    const text = v == null ? "" : String(v);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const csv = rows.map((r) => r.map(cell).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** A report's CSV name: what it is, its range and basis. */
export const csvName = (report: string, period: Pick<Period, "from" | "to" | "basis">, asAt?: string) =>
  `${report}-${asAt ?? `${period.from}-to-${period.to}`}-${period.basis.toLowerCase()}.csv`;

export function ExportButton({ onExport, disabled }: { onExport: () => void; disabled?: boolean }) {
  return (
    <Button variant="outline" size="sm" onClick={onExport} disabled={disabled}>
      <Download className="size-4" /> Export CSV
    </Button>
  );
}
